import { useState, useEffect } from 'react';
import homeSectionService from '../../../services/homeSectionService';

interface Section {
  id: string;
  name: string;
  description: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
  isVisible: boolean;
  order: number;
  type: 'solutions' | 'service' | 'process' | 'whyChoose' | 'project' | 'testimonials' | 'news' | 'businessChallenges';
}

const SectionsManagement = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    background: '',
    nameColor: '',
    descriptionColor: '',
    isVisible: true,
    order: 1,
    type: 'service' as Section['type']
  });
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const sectionTypes = [
    { value: 'solutions', label: 'Giải pháp' },
    { value: 'service', label: 'Dịch vụ' },
    { value: 'process', label: 'Quy trình' },
    { value: 'whyChoose', label: 'Tại sao chọn' },
    { value: 'project', label: 'Dự án' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'news', label: 'Tin tức' },
    { value: 'businessChallenges', label: 'Thách thức' }
  ];

  // Load sections từ API
  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await homeSectionService.getAll();
      if (response.success && response.data) {
        setSections(response.data);
      } else {
        setError(response.message || 'Không thể tải danh sách phần nội dung');
      }
    } catch (err: any) {
      console.error('Error loading sections:', err);
      const errorMessage = err.response?.data?.message 
        || err.message 
        || 'Có lỗi xảy ra khi tải dữ liệu';
      setError(errorMessage);
      
      // Log chi tiết để debug
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      } else if (err.request) {
        console.error('Request made but no response received:', err.request);
        setError('Không thể kết nối đến server. Vui lòng kiểm tra backend có đang chạy không.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingSection(null);
    setFormData({
      name: '',
      description: '',
      background: '',
      nameColor: '',
      descriptionColor: '',
      isVisible: true,
      order: sections.length > 0 ? Math.max(...sections.map(s => s.order)) + 1 : 1,
      type: 'service'
    });
    setShowModal(true);
  };

  const handleEdit = (section: Section) => {
    setIsCreating(false);
    setEditingSection(section);
    setFormData({
      name: section.name,
      description: section.description,
      background: section.background || '',
      nameColor: section.nameColor || '',
      descriptionColor: section.descriptionColor || '',
      isVisible: section.isVisible,
      order: section.order,
      type: section.type
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const data = {
        name: formData.name,
        description: formData.description,
        background: formData.background,
        nameColor: formData.nameColor,
        descriptionColor: formData.descriptionColor,
        isVisible: formData.isVisible,
        type: formData.type,
        order: formData.order
      };

      let response;
      if (isCreating) {
        // Tạo mới
        response = await homeSectionService.create(data);
      } else {
        // Cập nhật
        if (!editingSection) return;
        response = await homeSectionService.update(editingSection.id, data);
      }
      
      if (response.success) {
        await loadSections(); // Reload danh sách
        setShowModal(false);
        setEditingSection(null);
        setIsCreating(false);
      } else {
        setError(response.message || (isCreating ? 'Tạo mới thất bại' : 'Cập nhật thất bại'));
      }
    } catch (err: any) {
      console.error(`Error ${isCreating ? 'creating' : 'updating'} section:`, err);
      setError(err.response?.data?.message || `Có lỗi xảy ra khi ${isCreating ? 'tạo mới' : 'cập nhật'}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (id: string) => {
    const section = sections.find(s => s.id === id);
    if (!section) return;

    try {
      setError(null);
      const updateData = {
        isVisible: !section.isVisible
      };

      const response = await homeSectionService.update(id, updateData);
      
      if (response.success) {
        await loadSections(); // Reload danh sách
      } else {
        setError(response.message || 'Cập nhật trạng thái thất bại');
      }
    } catch (err: any) {
      console.error('Error toggling visibility:', err);
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const moveSection = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = sections.findIndex(s => s.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sections.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newSections = [...sections];
    
    // Swap orders
    const currentOrder = newSections[currentIndex].order;
    const targetOrder = newSections[targetIndex].order;
    
    // Optimistic update: Update UI ngay lập tức
    const updatedSections = [...sections];
    updatedSections[currentIndex] = { ...updatedSections[currentIndex], order: targetOrder };
    updatedSections[targetIndex] = { ...updatedSections[targetIndex], order: currentOrder };
    
    // Sort lại theo order
    updatedSections.sort((a, b) => a.order - b.order);
    
    // Update state ngay để không bị chớp
    setSections(updatedSections);
    setReordering(true);
    setError(null);

    try {
      const orders = updatedSections.map(s => ({
        id: s.id,
        order: s.order
      }));

      const response = await homeSectionService.reorder(orders);
      
      if (!response.success) {
        // Nếu API fail, revert lại state ban đầu
        setError(response.message || 'Sắp xếp lại thất bại');
        await loadSections(); // Reload để revert
      }
      // Nếu thành công, không cần reload vì đã update state rồi
    } catch (err: any) {
      console.error('Error reordering sections:', err);
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi sắp xếp lại');
      // Revert lại state ban đầu nếu có lỗi
      await loadSections();
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quản lý các phần nội dung</h2>
          <div className="text-sm text-gray-600 mt-1">
            Quản lý hiển thị và thứ tự các phần trên trang chủ
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Thêm mới
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phần nội dung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thứ tự
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sections.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    Chưa có phần nội dung nào
                  </td>
                </tr>
              ) : (
                sections.map((section, index) => (
                  <tr key={section.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {section.name}
                        </div>
                        {section.description && (
                          <div className="text-sm text-gray-500">
                            {section.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {sectionTypes.find(t => t.value === section.type)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleVisibility(section.id)}
                        disabled={reordering}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer whitespace-nowrap ${
                          section.isVisible
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        } ${reordering ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {section.isVisible ? 'Hiển thị' : 'Ẩn'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-900">{section.order}</span>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => moveSection(section.id, 'up')}
                            disabled={index === 0 || reordering}
                            className={`w-5 h-5 flex items-center justify-center cursor-pointer ${
                              index === 0 || reordering
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <i className="ri-arrow-up-s-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => moveSection(section.id, 'down')}
                            disabled={index === sections.length - 1 || reordering}
                            className={`w-5 h-5 flex items-center justify-center cursor-pointer ${
                              index === sections.length - 1 || reordering
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <i className="ri-arrow-down-s-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(section)}
                        disabled={reordering}
                        className={`text-teal-600 hover:text-teal-900 cursor-pointer ${
                          reordering ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isCreating ? 'Thêm phần nội dung mới' : 'Chỉnh sửa phần nội dung'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingSection(null);
                  setIsCreating(false);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên phần <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Tên hiển thị"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả <span className="text-gray-500 text-xs">(tối đa 150 ký tự)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Mô tả ngắn gọn"
                  maxLength={150}
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.description.length}/150 ký tự
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Background (màu)
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.background || '#ffffff'}
                    onChange={(e) => setFormData({ ...formData, background: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer disabled:opacity-50"
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.background}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Chỉ cho phép nhập mã màu hex
                      if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setFormData({ ...formData, background: value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="#ffffff"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Chỉ chấp nhận mã màu hex (ví dụ: #ffffff, #00c897)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Màu chữ tên phần
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.nameColor || '#000000'}
                    onChange={(e) => setFormData({ ...formData, nameColor: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer disabled:opacity-50"
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.nameColor}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Chỉ cho phép nhập mã màu hex
                      if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setFormData({ ...formData, nameColor: value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="#000000"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Để trống sẽ dùng màu mặc định</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Màu chữ mô tả
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.descriptionColor || '#000000'}
                    onChange={(e) => setFormData({ ...formData, descriptionColor: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer disabled:opacity-50"
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.descriptionColor}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Chỉ cho phép nhập mã màu hex
                      if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setFormData({ ...formData, descriptionColor: value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="#000000"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Để trống sẽ dùng màu mặc định</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loại phần <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as Section['type'] })}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={saving || !isCreating}
                >
                  {sectionTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {!isCreating && (
                  <p className="text-xs text-gray-500 mt-1">Không thể thay đổi loại phần sau khi đã tạo</p>
                )}
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isVisible}
                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                    className="mr-2"
                    disabled={saving}
                  />
                  <span className="text-sm font-medium text-gray-700">Hiển thị trên trang chủ</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingSection(null);
                  setIsCreating(false);
                  setError(null);
                }}
                disabled={saving}
                className={`px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className={`px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer ${
                  saving || !formData.name.trim() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? 'Đang lưu...' : isCreating ? 'Tạo mới' : 'Cập nhật'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionsManagement;
